"use client";
import { Api } from "@/Apis";
import { Html } from "@/Components";
import { formProps } from "@/Helpers/Constants";
import { useApp, useT } from "@/Hooks";
import useConstants from "@/Hooks/useConstants";
import { ContactUsSectionProps } from "@/Templates/SectionsProps";
import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Upload,
  UploadFile,
} from "antd";
import { FC, useEffect, useState } from "react";

export interface ContactUsCreateForm {
  Title: string;
  RequestTypeId: number;
  FirstName: string;
  LastName: string;
  PhoneNumber: string;
  Email: string;
  Text: string;
}

const ContactUs: FC<ContactUsSectionProps> = ({ setting, pageData }) => {
  const { sendButtonProps } = useConstants();
  const [form] = Form.useForm<ContactUsCreateForm>();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [requestTypes, setRequestTypes] = useState<
    { value: number; label: string }[]
  >([]);
  const { message } = useApp();
  const t = useT();
  useEffect(() => {
    Api.CommonSelect.getContactUsRequestTypes().then((result) =>
      setRequestTypes(
        (result.Result ?? [])
          .filter(
            (item): item is typeof item & { Value: number; Text: string } =>
              item.Value != null && item.Text != null,
          )
          .map((item) => ({ value: item.Value, label: item.Text })),
      ),
    );
  }, []);
  const onFinish = async (values: ContactUsCreateForm) => {
    setLoading(true);
    try {
      const r = await Api.LandingContactUs.create(
        values.Title,
        values.Text,
        values.PhoneNumber,
        values.FirstName,
        values.LastName,
        values.Email,
        values.RequestTypeId,
        fileList[0]?.originFileObj
          ? { data: fileList[0].originFileObj, fileName: fileList[0].name }
          : undefined,
      );
      if (r.Success) {
        message.success(
          t(
            "landing.contact.message.sentSuccessfully",
            undefined,
            "Your message was sent successfully.",
          ),
        );
        form.resetFields();
        setFileList([]);
      } else message.errors(r.Errors);
    } catch (error) {}
    setLoading(false);
  };
  return (
    <section className="contact-section">
      <div className="container">
        <div className="row">
          <div className="col-lg-5 col-md-12">
            <div className="section-inner-header contact-inner-header">
              <h6>{setting.Title}</h6>
              <h2>
                <Html value={setting.Description} />
              </h2>
            </div>
            <div className="card contact-card">
              <div className="card-body">
                <div className="contact-icon">
                  <i className="isax isax-location5" />
                </div>
                <div className="contact-details">
                  <h4>{t("common.field.address", undefined, "Address")}</h4>
                  <p>{pageData.CommonData.Address}</p>
                </div>
              </div>
            </div>
            {pageData.CommonData.Tels.map((tel) => (
              <div key={tel} className="card contact-card">
                <div className="card-body">
                  <div className="contact-icon">
                    <i className="isax isax-call5" />
                  </div>
                  <div className="contact-details">
                    <h4>{t("common.field.phoneNumber", undefined, "Phone")}</h4>
                    <p>{tel}</p>
                  </div>
                </div>
              </div>
            ))}
            <div className="card contact-card">
              <div className="card-body">
                <div className="contact-icon">
                  <i className="isax isax-sms5" />
                </div>
                <div className="contact-details">
                  <h4>{t("common.field.email", undefined, "Email")}</h4>
                  <p>{pageData.CommonData.Email}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-7 col-md-12">
            <Card
              hoverable
              loading={loading}
              title={t(
                "landing.contact.title.sendMessage",
                undefined,
                "Send message",
              )}
            >
              <Form {...formProps} onFinish={onFinish} form={form}>
                <Row>
                  {setting.Description && (
                    <Col span={24} style={{ marginBottom: 10 }}>
                      <Card type="inner">
                        <Html value={setting.Description} />
                      </Card>
                    </Col>
                  )}
                  <Col md={14} xs={24}>
                    <Form.Item
                      name="Title"
                      label={t("common.field.subject", undefined, "Subject")}
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col md={10} xs={24}>
                    <Form.Item
                      name="RequestTypeId"
                      label={t(
                        "common.field.requestType",
                        undefined,
                        "Request type",
                      )}
                      rules={[{ required: true }]}
                    >
                      <Select options={requestTypes} />
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item
                      name="FirstName"
                      label={t(
                        "common.field.firstName",
                        undefined,
                        "First name",
                      )}
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item
                      name="LastName"
                      label={t("common.field.lastName", undefined, "Last name")}
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item
                      name="PhoneNumber"
                      label={t("common.field.phoneNumber", undefined, "Phone")}
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col md={12} xs={24}>
                    <Form.Item
                      name="Email"
                      label={t("common.field.email", undefined, "Email")}
                      rules={[{ required: true, type: "email" }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item
                      name="Text"
                      label={t("common.field.text", undefined, "Text")}
                      rules={[{ required: true }]}
                    >
                      <Input.TextArea autoSize={{ minRows: 6 }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      label={t("common.field.file", undefined, "Attachment")}
                    >
                      <Upload
                        beforeUpload={() => false}
                        maxCount={1}
                        fileList={fileList}
                        onChange={({ fileList }) => setFileList(fileList)}
                      >
                        <Button>
                          {t("common.action.select", undefined, "Select file")}
                        </Button>
                      </Upload>
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Flex justify="center">
                      <Button {...sendButtonProps} size="large">
                        {t(
                          "landing.contact.action.sendMessage",
                          undefined,
                          "Send message",
                        )}
                      </Button>
                    </Flex>
                  </Col>
                </Row>
              </Form>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactUs;
